import { Table, tokPx } from '@sunwebgroup/ui-components'
import { Skeleton } from '@sunwebgroup/ui-components/uds'
import { eachDayOfInterval, format, isBefore, parseISO } from 'date-fns'
import { isEmpty } from 'ramda'
import { use, useCallback, useMemo } from 'react'
import { css, styled } from 'styled-components'
import { Day } from '@/scopes/prices/components/PriceTable/Day'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { PriceCell } from '@/scopes/prices/components/PriceTable/PriceCell'
import {
  getPriceRangeSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const DayHeader = styled(Table.Header)<{ $isHighlighted: boolean, $isSelected: boolean }>`
  padding: 0 ${tokPx('primitives_space_12')} 0 ${tokPx('primitives_space_12')};
  height: 66px;
  background: white;
  border: none;
  width: 100%;
  &:before {
    width: unset;
  }
`

const PriceTableColumn = styled(Table.Col)<{ $disabled?: boolean }>`
    margin-right: ${tokPx('primitives_space_6')};
    padding: 2.5px ${tokPx('primitives_space_6')};
    background: white;
    border-radius:  ${tokPx('primitives_corner_radius_m')};
    width: 100%;
    position:relative;
    ${({ $disabled }) => $disabled && css`
        opacity: 0.38;
    `}
`

const PriceTableBody = styled(Table.Body)`
    width: 100%;
    & div:last-child {
      margin-right: 0;
    }

    ${Table.Col}:last-child {
      border-radius: ${tokPx('primitives_corner_radius_m')};
    }

  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    margin-right: ${tokPx('primitives_space_6')};
  }
`

const ColumnSkeleton = styled(Skeleton)`
  width: 100%;
  height: 360px;
  margin: 2.5px 6px;
`

const PriceTableRoot = styled(Table.Root)`
    width: 100%;
`

export function Grid() {
  const { prices, pricesByDurationAndDateByWeeks } = usePricesSelector(pricesResponseSelector)
  const { dateFrom, dateTo } = usePricesSelector(getPriceRangeSelector)
  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const { selectPrice } = use(PricesActionsContext)
  const { navigationDateFrom, navigationDateTo, numberOfColumns } = use(NavigationByWeeksContext)
  const isLoading = usePricesSelector(isPriceTableLoadingStateSelector)

  const selectedPrice = useMemo(
    () => prices?.find(price => price.id === selectedPriceId),
    [prices, selectedPriceId],
  )

  const onPriceSelect = useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  if (!dateFrom || !dateTo || !navigationDateFrom || !navigationDateTo || !prices || isEmpty(prices)) {
    return (
      <PriceTableRoot data-qa="price-table">
        <PriceTableBody role="grid">
          {Array.from({ length: numberOfColumns }, (_, i) => (
            <ColumnSkeleton key={i} />
          ))}
        </PriceTableBody>
      </PriceTableRoot>
    )
  }

  const datesToDisplay = eachDayOfInterval({ start: navigationDateFrom, end: navigationDateTo }).map(date =>
    format(date, 'yyyy-MM-dd'),
  )

  const today = format(new Date(), 'yyyy-MM-dd')
  const disableColumn = (date: string) => isBefore(parseISO(date), parseISO(today))

  return (
    <PriceTableRoot data-qa="price-table">
      <PriceTableBody role="grid">
        {datesToDisplay.map(date => (
          isLoading
            ? <ColumnSkeleton key={date} />
            : (
                <PriceTableColumn key={date} $disabled={disableColumn(date)} role="row">
                  <DayHeader $isHighlighted={false} $isSelected={date === selectedPrice?.departureDate}>
                    <Day date={date} />
                  </DayHeader>
                  {!disableColumn(date) && Object.keys(pricesByDurationAndDateByWeeks!).map((duration: string) => (

                    <PriceCell
                      key={duration + date}
                      price={pricesByDurationAndDateByWeeks![Number(duration)]?.[date]}
                      onClick={onPriceSelect}
                      isSelected={selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration}
                    />
                  ))}
                </PriceTableColumn>
              )
        ))}
      </PriceTableBody>
    </PriceTableRoot>
  )
}
